import { ScriptProps, ScriptStatus } from './types/types';

export type RegistryEntry = {
    status: ScriptStatus;
    promise?: Promise<void>;
    error?: Error;
    element?: HTMLScriptElement;
};

const registry = new Map<string, RegistryEntry>();

/**
 *
 * @param props
 * @returns
 */
const makeKey = (props: ScriptProps) => {
    const { id, src, inline } = props;
    if (id) {
        return `id:${id}`;
    }

    if (src) {
        return `src:${src}`;
    }

    if (inline) {
        return `inline:${inline.slice(0, 64)}`;
    }
    throw new Error('Script requires id or src or inline');
};

const getScriptStatus = (key: string) => {
    return registry.get(key)?.status ?? 'idle';
};

/**
 *
 * @param props
 * @returns
 */
const loadScript = (props: ScriptProps) => {
    if (typeof document === 'undefined') {
        return Promise.resolve();
    }

    const key = makeKey(props);
    const existing = registry.get(key);

    if (existing?.status === 'ready') {
        return Promise.resolve();
    }

    if (existing?.status === 'loading' && existing?.promise) {
        return existing.promise;
    }

    if (existing?.status === 'error' && existing?.error) {
        return Promise.reject(existing?.error);
    }

    const entry: RegistryEntry = { status: 'loading' };

    const p = new Promise<void>((resolve, reject) => {
        const el = document.createElement('script');
        el.type = props.type ?? 'text/javascript';

        el.async = props.async ?? false;
        el.defer = props.defer ?? false;

        if (props.nonce) {
            el.nonce = props.nonce;
        }

        if (props.crossOrigin) {
            el.crossOrigin = props.crossOrigin;
        }

        if (props.refererPolicy) {
            el.referrerPolicy = props.refererPolicy;
        }

        if (props.attrs) {
            for (const [k, v] of Object.entries(props.attrs)) {
                el.setAttribute(k, v);
            }
        }

        if (props.src) {
            el.src = props.src;
        } else if (props.inline) {
            el.text = props.inline;
        }

        el.onload = () => {
            entry.status = 'ready';
            entry.element = el;
            resolve();
        };

        el.onerror = () => {
            entry.status = 'error';
            entry.error = new Error(`Failed to load script: ${props.src ?? '[inline]'} (${key})`);
            reject(entry.error);
        };

        document.head.appendChild(el);
    });

    entry.promise = p;
    registry.set(key, entry);

    return p;
};

const loadScripts = (props: ScriptProps[]): Promise<void> => {
    return props.reduce((acc, item) => acc.then(() => loadScript(item)), Promise.resolve());
};

export { getScriptStatus, loadScript, loadScripts };
